import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	FILTER_SEPARATOR,
	FILTER_VALUE_SEPARATOR,
	NEW_WAY_TO_ENCODING_FILTER_SIGN,
} from "go-list/core/components/Filter/services";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	AutofillStocktakingDraftApi,
	CustomFieldSearchSelectApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";

interface Props {
	isShown: boolean;
	onHide: () => void;
	stocktakingId: number;
	draftId: number;
	handleSuccessAction: () => void;
}

interface FormProps extends AutofillStocktakingDraftApi {
	custom_field: string;
	custom_field_value: string | number;
	category_custom_field: string;
	category_custom_field_value: string | number;
}

const AutofillDraftStateListModalForm = ({ isShown, onHide, draftId, stocktakingId, handleSuccessAction }: Props) => {
	const { t } = useTranslation();
	const form = useForm<FormProps>({
		criteriaMode: "all",
		defaultValues: {
			categories: [],
		},
	});
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		formState,
		formState: { errors },
		handleSubmit,
		setError,
		register,
		control,
		getValues,
		setValue,
	} = form;
	const productCustomFieldOptionsRef = useRef<CustomFieldSearchSelectApi[]>([]);
	const categoryCustomFieldOptionsRef = useRef<CustomFieldSearchSelectApi[]>([]);
	const [selectedProductCustomField, setSelectedProductCustomField] = useState<
		CustomFieldSearchSelectApi | undefined
	>();
	const [selectedCategoryCustomField, setSelectedCategoryCustomField] = useState<
		CustomFieldSearchSelectApi | undefined
	>();

	const searchCategories = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductCategorySearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const searchCustomFields = async (
		resourceType: string,
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => {
		params = {
			...params,
			"resource_type|e": resourceType,
		};
		return api.organization().getCustomFieldsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const renderCustomFieldValueInput = (
		customField: CustomFieldSearchSelectApi,
		fieldName: "custom_field_value" | "category_custom_field_value"
	) => {
		if (customField.opts.length > 0) {
			const options = customField.opts.map((x) => {
				return {
					value: x.value,
					label: x.value,
				};
			});
			return (
				<FormSelectGroup
					label={t("common.word.value")}
					name={fieldName}
					errors={errors}
					options={options}
					control={control}
					data-testid={fieldName}
					isMulti
				/>
			);
		}
		switch (customField.type) {
			case "TEXT":
				return (
					<FormInput label={t("common.word.value")} register={register} name={fieldName} errors={errors} />
				);
			case "NUMBER":
				return (
					<FormNumberInput
						label={t("common.word.value")}
						suffix={""}
						control={control}
						name={fieldName}
						errors={errors}
					/>
				);
			case "DATE":
				return (
					<FormDatePicker label={t("common.word.value")} control={control} name={fieldName} errors={errors} />
				);
			case "LONG_TEXT":
				return (
					<FormInput
						label={t("common.word.value")}
						register={register}
						as="textarea"
						name={fieldName}
						errors={errors}
						style={{ minHeight: "4rem" }}
					/>
				);
		}
	};

	const handleParseCustomFieldOptionValue = (
		option: CustomFieldSearchSelectApi,
		fieldName: "custom_field" | "category_custom_field"
	) => {
		if (fieldName === "custom_field") {
			productCustomFieldOptionsRef.current = productCustomFieldOptionsRef.current.find((o) => o.id === option.id)
				? [...productCustomFieldOptionsRef.current]
				: [...productCustomFieldOptionsRef.current, option];
		} else {
			categoryCustomFieldOptionsRef.current = categoryCustomFieldOptionsRef.current.find(
				(o) => o.id === option.id
			)
				? [...categoryCustomFieldOptionsRef.current]
				: [...categoryCustomFieldOptionsRef.current, option];
		}

		return option.id;
	};

	const handleUpdateSelectedCustomField = (
		fieldName: "custom_field" | "category_custom_field",
		valueFieldName: "custom_field_value" | "category_custom_field_value"
	) => {
		const id = getValues(fieldName);
		const option = (
			fieldName === "custom_field" ? productCustomFieldOptionsRef : categoryCustomFieldOptionsRef
		).current.find((o) => o.id === id);
		if (!option) {
			if (fieldName === "custom_field") setSelectedProductCustomField(undefined);
			else {
				setSelectedCategoryCustomField(undefined);
			}
		} else if (fieldName === "custom_field") setSelectedProductCustomField(option);
		else setSelectedCategoryCustomField(option);

		setValue(valueFieldName, "");
	};

	const handleFetchCategoriesBasedOnCustomField = async () => {
		const categoryCustomFieldValue = getValues("category_custom_field_value");

		const filter = `+${selectedCategoryCustomField!.slug}.PRODUCT_CATEGORY|e=${
			Array.isArray(categoryCustomFieldValue)
				? categoryCustomFieldValue.join(FILTER_VALUE_SEPARATOR)
				: categoryCustomFieldValue
		}`;
		const params = {
			f: btoa(unescape(encodeURIComponent(NEW_WAY_TO_ENCODING_FILTER_SIGN + filter))),
		};
		const res = await apiOrganization.getProductCategories(params);
		return res.data;
	};

	const getParsedRequestFilter = () => {
		let filters = FILTER_SEPARATOR;
		if (selectedProductCustomField) {
			const customFieldValue = getValues("custom_field_value");
			let filterValue;
			if (selectedProductCustomField.type === "DATE")
				filterValue = `bt=${customFieldValue}${FILTER_VALUE_SEPARATOR}${customFieldValue}`;
			if (selectedProductCustomField.type === "TEXT" || selectedProductCustomField.type === "NUMBER")
				filterValue = `e=${customFieldValue}`;
			const customField = selectedProductCustomField.opts.length
				? `+${selectedProductCustomField?.slug}.PRODUCT|${filterValue}`
				: `+${selectedProductCustomField?.slug}|${filterValue}`;
			filters += customField;
		}

		return filters;
	};

	const onSubmit = handleSubmit(async (data: FormProps) => {
		setLoading(true);
		try {
			let filter = getParsedRequestFilter();
			if (
				selectedCategoryCustomField?.slug !== undefined &&
				getValues("category_custom_field_value") !== undefined &&
				getValues("category_custom_field_value") !== null
			) {
				const categories = await handleFetchCategoriesBasedOnCustomField();
				if (categories.length > 0) {
					filter = `${FILTER_SEPARATOR}categories|e=${categories
						.map((category) => category.id)
						.join(FILTER_VALUE_SEPARATOR)}`;
					await api.organization().autofillStocktakingDraft(stocktakingId, draftId, data, {
						f: btoa(unescape(encodeURIComponent(NEW_WAY_TO_ENCODING_FILTER_SIGN + filter))),
					});
					handleSuccessAction();
					addSuccessFlash(t("lib:common.flash.saved"));
					onHide();
				} else {
					addSuccessFlash(t("lib:common.flash.saved"));
					onHide();
				}
			} else {
				await api.organization().autofillStocktakingDraft(stocktakingId, draftId, data, {
					f: btoa(unescape(encodeURIComponent(NEW_WAY_TO_ENCODING_FILTER_SIGN + filter))),
				});
				handleSuccessAction();
				addSuccessFlash(t("lib:common.flash.saved"));
				onHide();
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal show={isShown} onHide={onHide}>
			<FormDirty formState={formState} key="autofill-draft-form">
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.stocktaking_draft.field.autofill_draft.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormCheck
						type="switch"
						errors={errors}
						name="only_active"
						help={t("modules.stocktaking_draft.field.only_active.help_text")}
						label={t("modules.stocktaking_draft.field.only_active.title")}
						register={register}
					/>
					<FormCheck
						type="switch"
						errors={errors}
						name="use_actual_states"
						label={t("modules.stocktaking_draft.field.use_actual_states.title")}
						register={register}
					/>
					<FormCheck
						type="switch"
						errors={errors}
						name="update_only_when_stock_state_is_different_than_zero"
						label={t(
							"modules.stocktaking_draft.field.update_only_when_stock_state_is_different_than_zero.title"
						)}
						register={register}
					/>
					<FormCheck
						type="switch"
						errors={errors}
						name="volume_one"
						help={t("modules.stocktaking_draft.field.volume_one.help_text")}
						label={t("modules.stocktaking_draft.field.volume_one.title")}
						register={register}
					/>
					<FormSelectGroup
						control={control}
						loadOptions={searchCategories}
						label={t("modules.stocktaking_draft.field.categories.title")}
						name="categories"
						errors={errors}
						isMulti
						getOptionValue={(opt) => opt.id}
						data-testid="categories"
					/>
					<FormSelectGroup
						errors={errors}
						name="custom_field"
						loadOptions={(search: string, params: Record<string, any>, options?: Record<string, any>) =>
							searchCustomFields("PRODUCT", search, params, options)
						}
						control={control}
						getOptionLabel={(opt) => opt.label}
						getOptionValue={(obj) => handleParseCustomFieldOptionValue(obj, "custom_field")}
						onChange={() => handleUpdateSelectedCustomField("custom_field", "custom_field_value")}
						label={t("modules.stocktaking_draft.field.product_custom_field.title")}
						data-testid="product_custom_field"
					/>
					{selectedProductCustomField &&
						renderCustomFieldValueInput(selectedProductCustomField, "custom_field_value")}
					<FormSelectGroup
						errors={errors}
						name="category_custom_field"
						loadOptions={(search: string, params: Record<string, any>, options?: Record<string, any>) =>
							searchCustomFields("PRODUCT_CATEGORY", search, params, options)
						}
						control={control}
						getOptionLabel={(opt) => opt.label}
						getOptionValue={(obj) => handleParseCustomFieldOptionValue(obj, "category_custom_field")}
						onChange={() =>
							handleUpdateSelectedCustomField("category_custom_field", "category_custom_field_value")
						}
						label={t("modules.stocktaking_draft.field.category_custom_field.title")}
						data-testid="category_custom_field"
					/>
					{selectedCategoryCustomField &&
						renderCustomFieldValueInput(selectedCategoryCustomField, "category_custom_field_value")}
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.save")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default AutofillDraftStateListModalForm;
